import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AppLayout } from "@/components/intel/Layout";
import { ModuleShell, StatTile } from "@/components/intel/ModuleShell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  runEmailIntel,
  listEmailIntelReports,
  getEmailIntelReport,
  getEmailSourceHealth,
} from "@/lib/osint/email/email.functions";
import type { EmailIntelReport, VerificationState } from "@/lib/osint/email/types";
import { Loader2, Mail, ShieldCheck, ExternalLink } from "lucide-react";

export const Route = createFileRoute("/email")({
  head: () => ({
    meta: [
      { title: "Email Intelligence · CARIO Nexus" },
      {
        name: "description",
        content:
          "Investigate an email address from public sources only: domain and mail policy, public account signals, web references, documents, organisation contacts and predicted addresses — each with its source.",
      },
      { property: "og:title", content: "Email Intelligence · CARIO Nexus" },
      {
        property: "og:description",
        content: "Public-source email investigation with full provenance, deterministic confidence and a knowledge graph.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search["q"] === "string" && search["q"] ? { q: search["q"] } : {},
  component: EmailIntelPage,
});

const STATE_STYLE: Record<VerificationState, { label: string; className: string }> = {
  verified_public: { label: "VERIFIED PUBLIC", className: "bg-success/15 text-success border-success/40" },
  publicly_observed: { label: "PUBLICLY OBSERVED", className: "bg-cyan/15 text-cyan border-cyan/40" },
  predicted: { label: "PREDICTED", className: "bg-amber/15 text-amber border-amber/40" },
  unverified_candidate: { label: "UNVERIFIED CANDIDATE", className: "bg-muted/30 text-muted-foreground border-panel-border" },
};

function StateBadge({ state }: { state: VerificationState }) {
  const s = STATE_STYLE[state];
  return <span className={`rounded border px-1.5 py-0.5 font-mono text-[10px] tracking-wider ${s.className}`}>{s.label}</span>;
}

function Panel({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="glass rounded-xl p-3 sm:p-4">
      <h2 className="font-display text-sm">{title}</h2>
      {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function KeyVal({ items }: { items: [string, React.ReactNode][] }) {
  return (
    <dl className="grid gap-2 sm:grid-cols-2">
      {items.map(([k, v]) => (
        <div key={k} className="rounded-md border border-panel-border bg-background/40 p-2.5 min-w-0">
          <dt className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{k}</dt>
          <dd className="mt-0.5 break-words text-sm">{v ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

function Evidence({ items }: { items: EmailIntelReport["subject"]["evidence"] }) {
  return (
    <ul className="space-y-1.5">
      {items.map((e, i) => (
        <li key={`${e.source}-${i}`} className="rounded-md border border-panel-border bg-background/40 p-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-cyan">{e.kind.replace(/_/g, " ")}</span>
            <span className="text-xs">{e.source}</span>
          </div>
          {e.detail && <p className="mt-0.5 text-xs text-muted-foreground">{e.detail}</p>}
          {e.sourceUrl && (
            <a href={e.sourceUrl} target="_blank" rel="noreferrer noopener" className="mt-1 inline-flex items-center gap-1 break-all text-[11px] text-cyan hover:underline">
              <ExternalLink className="h-3 w-3 shrink-0" /> {e.sourceUrl}
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

function EmailRow({ entity }: { entity: EmailIntelReport["subject"] }) {
  return (
    <details className="rounded-md border border-panel-border bg-background/40 p-2.5">
      <summary className="flex cursor-pointer flex-wrap items-center gap-2">
        <span className="break-all font-mono text-xs">{entity.address}</span>
        <StateBadge state={entity.verificationState} />
        <span className="ml-auto font-mono text-[11px] text-muted-foreground">{Math.round(entity.confidence * 100)}%</span>
      </summary>
      <div className="mt-2 space-y-2">
        {(entity.displayName || entity.role) && (
          <p className="text-xs text-muted-foreground">
            {entity.displayName}
            {entity.role ? ` · ${entity.role}` : ""}
          </p>
        )}
        <ul className="list-disc pl-4 text-[11px] text-muted-foreground">
          {entity.confidenceReasons.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
        <Evidence items={entity.evidence} />
      </div>
    </details>
  );
}

function EmailIntelPage() {
  const run = useServerFn(runEmailIntel);
  const list = useServerFn(listEmailIntelReports);
  const get = useServerFn(getEmailIntelReport);
  const health = useServerFn(getEmailSourceHealth);

  const { q } = Route.useSearch();
  const [email, setEmail] = useState(q ?? "");
  const [depth, setDepth] = useState<"quick" | "standard" | "deep">("standard");
  const [report, setReport] = useState<EmailIntelReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const history = useQuery({ queryKey: ["email-intel-history"], queryFn: () => list() });
  const sourceHealth = useQuery({ queryKey: ["email-source-health"], queryFn: () => health(), staleTime: 300_000 });

  const mutation = useMutation({
    mutationFn: (vars: { email: string; depth: "quick" | "standard" | "deep" }) => run({ data: vars }),
    onSuccess: (res) => {
      setReport(res.report);
      setError(null);
      void history.refetch();
    },
    onError: (e: Error) => setError(e.message),
  });

  const open = useMutation({
    mutationFn: (id: string) => get({ data: { id } }),
    onSuccess: (res) => setReport(res.report),
  });

  const verifiedMentions = useMemo(() => (report?.mentions ?? []).filter((m) => m.emailVisibleOnPage), [report]);

  return (
    <AppLayout>
      <ModuleShell
        eyebrow="CARIO NEXUS"
        title="Email Intelligence"
        description="Public-source only. No mailbox probing, no verification email, no private account access — every finding carries the exact source it came from."
        meta={
          report && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <StatTile label="Confidence" value={`${Math.round(report.subject.confidence * 100)}%`} />
              <StatTile label="Sources" value={report.sources.filter((s) => s.status === "ok").length} />
              <StatTile label="Requests" value={report.stats.requests} tone="amber" />
              <StatTile label="Evidence" value={report.stats.evidenceRecords} tone="success" />
            </div>
          )
        }
      >
        <section className="glass rounded-xl p-3 sm:p-4">
          <form
            className="flex flex-col gap-2 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault();
              const value = email.trim().toLowerCase();
              if (!/^[a-z0-9._%+'-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(value)) {
                setError("Enter a valid email address");
                return;
              }
              setError(null);
              mutation.mutate({ email: value, depth });
            }}
          >
            <div className="relative flex-1">
              <Mail className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                inputMode="email"
                className="h-11 w-full rounded-md border border-panel-border bg-background/60 pl-9 pr-3 text-sm outline-none focus:border-cyan/60"
              />
            </div>
            <select
              value={depth}
              onChange={(e) => setDepth(e.target.value as typeof depth)}
              className="h-11 rounded-md border border-panel-border bg-background/60 px-3 text-sm"
            >
              <option value="quick">Quick</option>
              <option value="standard">Standard</option>
              <option value="deep">Deep</option>
            </select>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-cyan px-4 text-sm font-medium text-background disabled:opacity-60"
            >
              {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Investigate
            </button>
          </form>
          {error && <p className="mt-2 text-xs text-danger">{error}</p>}
          {mutation.isPending && (
            <p className="mt-2 text-xs text-muted-foreground">
              Running domain intelligence, public account signals, web references, code metadata and organisation discovery…
            </p>
          )}
          {!!history.data?.length && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {history.data.slice(0, 12).map((h) => (
                <button
                  key={h.id}
                  onClick={() => open.mutate(h.id)}
                  className="rounded border border-panel-border bg-background/40 px-2 py-1 font-mono text-[11px] hover:border-cyan/50"
                >
                  {h.email}
                </button>
              ))}
            </div>
          )}
        </section>

        {report && (
          <Tabs defaultValue="overview" className="mt-3">
            <div className="overflow-x-auto">
              <TabsList className="flex w-max">
                {[
                  ["overview", "Overview"],
                  ["identity", "Identity"],
                  ["accounts", "Public accounts"],
                  ["organization", "Organization"],
                  ["related", "Related emails"],
                  ["mentions", "Public mentions"],
                  ["documents", "Documents"],
                  ["domain", "Domain"],
                  ["infrastructure", "Infrastructure"],
                  ["evidence", "Evidence"],
                  ["graph", "Graph"],
                  ["ai", "AI analysis"],
                  ["sources", "Sources"],
                ].map(([v, label]) => (
                  <TabsTrigger key={v} value={v}>
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <TabsContent value="overview" className="space-y-3">
              <Panel title="Subject address">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="break-all font-mono text-sm">{report.email}</span>
                  <StateBadge state={report.subject.verificationState} />
                </div>
                <KeyVal
                  items={[
                    ["Local part", report.localPart],
                    ["Domain", report.domain],
                    ["Canonical", report.syntax.normalized],
                    ["Sub-address tag", report.syntax.plusTag ?? "none"],
                    ["Mail provider", report.domainIntel.dns.provider ?? "unknown"],
                    ["Organisation", report.domainIntel.organization ?? "not published"],
                    ["Free-mail provider", report.domainIntel.dns.isFreeMail ? "yes" : "no"],
                    ["Disposable provider", report.domainIntel.dns.isDisposable ? "yes" : "no"],
                  ]}
                />
                {!!report.syntax.issues.length && (
                  <ul className="mt-2 list-disc pl-4 text-xs text-amber">
                    {report.syntax.issues.map((i) => (
                      <li key={i}>{i}</li>
                    ))}
                  </ul>
                )}
              </Panel>
              <Panel title="What CARIO established" subtitle="Deterministic statements derived from the evidence, before any language model runs.">
                <ul className="space-y-1.5">
                  {report.analyst.statements.map((s, i) => (
                    <li key={i} className="flex flex-wrap items-start gap-2 text-sm">
                      <Badge variant="outline" className="font-mono text-[10px]">
                        {s.classification}
                      </Badge>
                      <span className="min-w-0 flex-1">{s.text}</span>
                    </li>
                  ))}
                  {!report.analyst.statements.length && <li className="text-sm text-muted-foreground">No public evidence was found for this address.</li>}
                </ul>
              </Panel>
            </TabsContent>

            <TabsContent value="identity">
              <Panel title="Identity candidates" subtitle="BELONGS_TO requires direct public evidence; everything weaker stays POSSIBLY_BELONGS_TO.">
                {report.identity.length ? (
                  <ul className="space-y-2">
                    {report.identity.map((p) => (
                      <li key={p.name} className="rounded-md border border-panel-border bg-background/40 p-2.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm">{p.name}</span>
                          <Badge variant="outline" className="font-mono text-[10px]">{p.relation}</Badge>
                          <span className="ml-auto font-mono text-[11px] text-muted-foreground">{Math.round(p.confidence * 100)}%</span>
                        </div>
                        {(p.organization || p.role) && (
                          <p className="mt-0.5 text-xs text-muted-foreground">{[p.role, p.organization].filter(Boolean).join(" · ")}</p>
                        )}
                        <div className="mt-2">
                          <Evidence items={p.evidence} />
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">No person could be linked to this address from public evidence.</p>
                )}
              </Panel>
            </TabsContent>

            <TabsContent value="accounts" className="space-y-3">
              <Panel title="Public account signals" subtitle="Unauthenticated public lookups only. No recovery flows, no password resets, no private data.">
                <ul className="space-y-1.5">
                  {report.accountSignals.map((s, i) => (
                    <li key={`${s.platform}-${i}`} className="rounded-md border border-panel-border bg-background/40 p-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm">{s.platform}</span>
                        <Badge variant="outline" className={`font-mono text-[10px] ${s.status === "present" ? "text-success" : "text-muted-foreground"}`}>
                          {s.status.toUpperCase()}
                        </Badge>
                        <span className="ml-auto font-mono text-[11px] text-muted-foreground">{Math.round(s.confidence * 100)}%</span>
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{s.evidence}</p>
                      {s.publicProfileUrl && (
                        <a href={s.publicProfileUrl} target="_blank" rel="noreferrer noopener" className="break-all text-[11px] text-cyan hover:underline">
                          {s.publicProfileUrl}
                        </a>
                      )}
                    </li>
                  ))}
                  {!report.accountSignals.length && <li className="text-sm text-muted-foreground">No public account surface found.</li>}
                </ul>
              </Panel>
              <Panel title="Username pivots" subtitle="Derived from the local part. A matching username never proves the same owner.">
                <ul className="space-y-2">
                  {report.usernames.map((u) => (
                    <li key={u.value} className="rounded-md border border-panel-border bg-background/40 p-2.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-sm">{u.value}</span>
                        <span className="text-xs text-muted-foreground">from {u.derivedFrom}</span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {u.accounts.map((a) => (
                          <a key={a.url} href={a.url} target="_blank" rel="noreferrer noopener" className="rounded border border-panel-border px-2 py-0.5 text-[11px] hover:border-cyan/50">
                            {a.platform}
                          </a>
                        ))}
                        {!u.accounts.length && <span className="text-[11px] text-muted-foreground">no public accounts matched</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              </Panel>
              <Panel title="Public GitHub metadata">
                <div className="space-y-2">
                  {report.github.accounts.map((a) => (
                    <div key={a.login} className="rounded-md border border-panel-border bg-background/40 p-2">
                      <a href={a.url} target="_blank" rel="noreferrer noopener" className="text-sm text-cyan hover:underline">
                        {a.login}
                      </a>
                      <p className="text-xs text-muted-foreground">{[a.name, a.company, a.location].filter(Boolean).join(" · ") || a.evidence}</p>
                    </div>
                  ))}
                  {report.github.commits.map((c) => (
                    <a key={c.url} href={c.url} target="_blank" rel="noreferrer noopener" className="block break-all rounded-md border border-panel-border bg-background/40 p-2 text-xs hover:border-cyan/50">
                      {c.repo} — {c.detail}
                    </a>
                  ))}
                  {!report.github.accounts.length && !report.github.commits.length && (
                    <p className="text-sm text-muted-foreground">No public GitHub metadata exposes this address.</p>
                  )}
                </div>
              </Panel>
            </TabsContent>

            <TabsContent value="organization" className="space-y-3">
              <Panel title="Organisation contact map" subtitle={report.organizationMap.organization ?? report.domain}>
                <ul className="space-y-1.5">
                  {report.organizationMap.contacts.map((c) => (
                    <li key={c.address} className="rounded-md border border-panel-border bg-background/40 p-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="break-all font-mono text-xs">{c.address}</span>
                        <StateBadge state={c.verificationState} />
                      </div>
                      <p className="text-xs text-muted-foreground">{[c.person, c.role].filter(Boolean).join(" · ") || "no name published beside this address"}</p>
                      {c.sourceUrl && (
                        <a href={c.sourceUrl} target="_blank" rel="noreferrer noopener" className="break-all text-[11px] text-cyan hover:underline">
                          {c.sourceUrl}
                        </a>
                      )}
                    </li>
                  ))}
                  {!report.organizationMap.contacts.length && <li className="text-sm text-muted-foreground">No organisation contacts discovered.</li>}
                </ul>
              </Panel>
              <Panel title="Email naming convention" subtitle="Inferred from verified public addresses. Generated addresses are predictions and are never marked verified.">
                {report.patterns.length ? (
                  <ul className="space-y-1.5">
                    {report.patterns.map((p) => (
                      <li key={p.pattern} className="rounded-md border border-panel-border bg-background/40 p-2 text-sm">
                        <span className="font-mono text-xs">{p.label}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          {p.support} observed · confidence {Math.round(p.confidence * 100)}%
                        </span>
                        <div className="mt-1 break-all font-mono text-[11px] text-muted-foreground">{p.examples.join(", ")}</div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">Not enough verified public addresses to infer a convention.</p>
                )}
                {!!report.predictedEmails.length && (
                  <div className="mt-3 space-y-1.5">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-amber">Predicted addresses — not facts</div>
                    {report.predictedEmails.map((p) => (
                      <EmailRow key={p.address} entity={p} />
                    ))}
                  </div>
                )}
              </Panel>
            </TabsContent>

            <TabsContent value="related">
              <Panel title="Related public addresses" subtitle="Deduplicated: one entity per address, with every source attached.">
                <div className="space-y-1.5">
                  {report.relatedEmails.map((e) => (
                    <EmailRow key={e.address} entity={e} />
                  ))}
                  {!report.relatedEmails.length && <p className="text-sm text-muted-foreground">No related addresses discovered.</p>}
                </div>
              </Panel>
            </TabsContent>

            <TabsContent value="mentions">
              <Panel title="Public web references" subtitle={`${verifiedMentions.length} of ${report.mentions.length} confirmed by fetching the live page.`}>
                <ul className="space-y-1.5">
                  {report.mentions.map((m) => (
                    <li key={m.url} className="rounded-md border border-panel-border bg-background/40 p-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm">{m.title ?? m.domain}</span>
                        <Badge variant="outline" className={`font-mono text-[10px] ${m.emailVisibleOnPage ? "text-success" : "text-muted-foreground"}`}>
                          {m.emailVisibleOnPage ? "CONFIRMED ON PAGE" : "SNIPPET ONLY"}
                        </Badge>
                      </div>
                      {m.snippet && <p className="mt-0.5 text-xs text-muted-foreground">{m.snippet}</p>}
                      <a href={m.url} target="_blank" rel="noreferrer noopener" className="break-all text-[11px] text-cyan hover:underline">
                        {m.url}
                      </a>
                    </li>
                  ))}
                  {!report.mentions.length && <li className="text-sm text-muted-foreground">No public references found.</li>}
                </ul>
              </Panel>
            </TabsContent>

            <TabsContent value="documents">
              <Panel title="Public documents containing addresses">
                <ul className="space-y-1.5">
                  {report.documents.map((d) => (
                    <li key={d.url} className="rounded-md border border-panel-border bg-background/40 p-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm">{d.title ?? d.url}</span>
                        <Badge variant="outline" className="font-mono text-[10px]">{d.type.toUpperCase()}</Badge>
                      </div>
                      <p className="mt-0.5 break-all font-mono text-[11px] text-muted-foreground">{d.emails.join(", ")}</p>
                      <a href={d.url} target="_blank" rel="noreferrer noopener" className="break-all text-[11px] text-cyan hover:underline">
                        {d.url}
                      </a>
                    </li>
                  ))}
                  {!report.documents.length && <li className="text-sm text-muted-foreground">No public documents exposed this address.</li>}
                </ul>
              </Panel>
            </TabsContent>

            <TabsContent value="domain">
              <Panel title="Mail domain technical facts" subtitle="Technical intelligence is stored separately from identity findings. No SMTP probing is performed.">
                <KeyVal
                  items={[
                    ["MX", report.domainIntel.dns.mx.join(", ") || "none"],
                    ["Accepts mail", report.domainIntel.dns.acceptsMail ? "yes" : "no"],
                    ["SPF", report.domainIntel.dns.spf.join(" ") || "not published"],
                    ["DMARC", report.domainIntel.dns.dmarc.join(" ") || "not published"],
                    ["DMARC policy", report.domainIntel.dns.dmarcPolicy ?? "none"],
                    ["Nameservers", report.domainIntel.dns.ns.join(", ") || "—"],
                    ["A records", report.domainIntel.dns.a.join(", ") || "—"],
                    ["Registrar", report.domainIntel.rdap?.registrar ?? "—"],
                    ["Registered", report.domainIntel.rdap?.created ?? "—"],
                    ["Expires", report.domainIntel.rdap?.expires ?? "—"],
                    ["Registry status", report.domainIntel.rdap?.statuses.join(", ") || "—"],
                    ["Website", report.domainIntel.website?.url ?? "—"],
                  ]}
                />
              </Panel>
            </TabsContent>

            <TabsContent value="infrastructure" className="space-y-3">
              <Panel title="Certificates">
                <ul className="space-y-1 text-xs">
                  {report.domainIntel.certificates.slice(0, 20).map((c, i) => (
                    <li key={`${c.name}-${i}`} className="break-all rounded border border-panel-border bg-background/40 p-2">
                      {c.name} {c.issuer ? `· ${c.issuer}` : ""} {c.notBefore ? `· ${c.notBefore}` : ""}
                    </li>
                  ))}
                  {!report.domainIntel.certificates.length && <li className="text-sm text-muted-foreground">No certificates found.</li>}
                </ul>
              </Panel>
              <Panel title={`Hostnames (${report.domainIntel.subdomains.length})`}>
                <div className="flex flex-wrap gap-1.5">
                  {report.domainIntel.subdomains.slice(0, 200).map((s) => (
                    <span key={s} className="rounded border border-panel-border px-1.5 py-0.5 font-mono text-[11px]">{s}</span>
                  ))}
                  {!report.domainIntel.subdomains.length && <span className="text-sm text-muted-foreground">None discovered.</span>}
                </div>
              </Panel>
            </TabsContent>

            <TabsContent value="evidence">
              <Panel title="Evidence chain for the subject address" subtitle="Every email entity in this report answers: where did CARIO find this?">
                <Evidence items={report.subject.evidence} />
              </Panel>
            </TabsContent>

            <TabsContent value="graph">
              <Panel title="Knowledge graph" subtitle={`${report.graph.nodes.length} nodes · ${report.graph.edges.length} relationships`}>
                <div className="max-h-[520px] space-y-1 overflow-y-auto">
                  {report.graph.edges.map((e, i) => {
                    const from = report.graph.nodes.find((n) => n.id === e.from);
                    const to = report.graph.nodes.find((n) => n.id === e.to);
                    return (
                      <div key={i} className="flex flex-wrap items-center gap-2 rounded border border-panel-border bg-background/40 p-2 text-xs">
                        <span className="font-mono text-[10px] text-muted-foreground">{from?.type}</span>
                        <span className="break-all">{from?.label}</span>
                        <span className="font-mono text-[10px] text-cyan">—{e.kind}→</span>
                        <span className="font-mono text-[10px] text-muted-foreground">{to?.type}</span>
                        <span className="break-all">{to?.label}</span>
                        <span className="ml-auto font-mono text-[10px] text-muted-foreground">{Math.round(e.confidence * 100)}%</span>
                      </div>
                    );
                  })}
                </div>
              </Panel>
            </TabsContent>

            <TabsContent value="ai">
              <Panel title="CARIO AI Analyst" subtitle={report.analyst.model ? `Model: ${report.analyst.model}` : report.analyst.error}>
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{report.analyst.summary}</p>
              </Panel>
            </TabsContent>

            <TabsContent value="sources">
              <div className="grid gap-3 lg:grid-cols-2">
                <Panel title="Sources used in this investigation">
                  <ul className="space-y-1 text-xs">
                    {report.sources.map((s) => (
                      <li key={s.id} className="flex flex-wrap items-center gap-2 rounded border border-panel-border bg-background/40 p-2">
                        <span>{s.label}</span>
                        <Badge
                          variant="outline"
                          className={`font-mono text-[10px] ${s.status === "ok" ? "text-success" : s.status === "error" ? "text-danger" : "text-muted-foreground"}`}
                        >
                          {s.status.toUpperCase()}
                        </Badge>
                        <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                          {s.findings} findings · {s.requests} req · {s.latencyMs}ms
                        </span>
                        {s.error && <span className="w-full text-[11px] text-muted-foreground">{s.error}</span>}
                      </li>
                    ))}
                  </ul>
                </Panel>
                <Panel title="Source registry health" subtitle="Live availability of every email adapter. No investigated address is sent during health checks.">
                  <ul className="space-y-1 text-xs">
                    {(sourceHealth.data ?? []).map((s) => (
                      <li key={s.id} className="flex flex-wrap items-center gap-2 rounded border border-panel-border bg-background/40 p-2">
                        <span>{s.label}</span>
                        <Badge variant="outline" className="font-mono text-[10px]">{s.category}</Badge>
                        <span className={`ml-auto font-mono text-[10px] ${s.status === "up" ? "text-success" : s.status === "down" ? "text-danger" : "text-muted-foreground"}`}>
                          {s.status.toUpperCase()} · {s.latencyMs}ms
                        </span>
                      </li>
                    ))}
                    {sourceHealth.isLoading && <li className="text-muted-foreground">Checking adapters…</li>}
                  </ul>
                </Panel>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </ModuleShell>
    </AppLayout>
  );
}
